//#region import libraries
import React, { useEffect, useState } from 'react';

// bootstrap
import { Badge } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';

// tremor
import { Subtitle, Title } from '@tremor/react'
//icons
import { FaRegEye, FaRegCopy, FaQrcode, FaPlus, FaChevronDown, FaChevronRight, FaClipboardList, FaRegTrashCan, FaArrowUpRightFromSquare } from 'react-icons/fa6';

// custom components
import { CustomSpinner, CustomQrCode } from '_atoms';
import { CustomTooltip, QuestionComponent } from '_organisms';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectQuestions, getQuestions, updateSurveyAvailability, selectPostQuestionnaireStatus, resetPostSurveyFlags, selectUpdateSurveyStatus, selectQuestionsStatus, deleteSurvey, selectDeleteSurveyStatus } from '_redux/slices/analytics/review-control-panel';
import { selectIata } from '_redux/slices/main-screen/user';

// STYLING
import { Colors } from '_styles';
import SurveyQuestionnaireForm from '../../../../components/organisms/feedback/survey-questionnaire-form';
import { surveyToDraft } from '../../../../components/organisms/feedback/survey-validation';
import { ActionsModal } from '_components/molecules';

//#endregion

const BORDER = '#e5e7eb';
const MUTED = '#6b7280';

const StatusPill = ({ active }) => (
    <span
        style={{
            fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 12,
            background: active ? '#E8F5E9' : '#F3F4F6',
            color: active ? '#2E7D32' : MUTED,
        }}
    >
        {active ? 'Active' : 'Draft'}
    </span>
);

const ReviewControlPanel = () => {

    // states
    const [showCreateSurvey, setShowCreateSurvey] = useState(false);
    const [draft, setDraft] = useState(null);           // pre-filled draft when duplicating
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [pendingId, setPendingId] = useState(null);   // survey whose availability is updating
    const [showModal, setShowModal] = useState(false);
    const [fileName, setFileName] = useState("");
    const [surveyTitle, setSurveyTitle] = useState("");
    const [link, setLink] = useState("")

    // redux
    const dispatch = useDispatch();

    const iata = useSelector(selectIata);
    const questions = useSelector(selectQuestions);
    const postQuestionnaireStatus = useSelector(selectPostQuestionnaireStatus);
    const updateSurveyStatus = useSelector(selectUpdateSurveyStatus);
    const questionsStatus = useSelector(selectQuestionsStatus);
    const deleteSurveyStatus = useSelector(selectDeleteSurveyStatus);
    const [deleteTarget, setDeleteTarget] = useState(null);   // survey awaiting delete confirmation

    // Deleted surveys are kept server-side for past answers but hidden here.
    const surveys = Array.isArray(questions) ? questions.filter((s) => s && Array.isArray(s.questions) && !s.deleted) : [];

    useEffect(() => {
        // get questions
        const data = { iata, passenger: false }
        dispatch(getQuestions(data))
    }, []);

    useEffect(() => {
        if (postQuestionnaireStatus === "succeeded" || updateSurveyStatus === "succeeded" || deleteSurveyStatus === "succeeded") {
            dispatch(resetPostSurveyFlags());
            setShowCreateSurvey(false);
            setDraft(null);
            setDeleteTarget(null);
            const data = { iata, passenger: false }
            dispatch(getQuestions(data))
        };
        if (updateSurveyStatus !== "loading") setPendingId(null);
    }, [postQuestionnaireStatus, updateSurveyStatus, deleteSurveyStatus])


    const createSurvey = () => {
        setDraft(null);
        setShowCreateSurvey(true);
    }

    const duplicateSurvey = (survey) => {
        setDraft(surveyToDraft(survey));
        setShowCreateSurvey(true);
    }

    const activateSurvey = (surveyId, active) => {
        setPendingId(surveyId);
        const data = { surveyId, active, iata }
        dispatch(updateSurveyAvailability(data))
    }

    const confirmDelete = () => {
        if (!deleteTarget) return;
        dispatch(deleteSurvey({ iata, surveyId: deleteTarget._id }))
    }

    const closeDelete = () => {
        if (deleteSurveyStatus === "loading") return;
        setDeleteTarget(null);
        dispatch(resetPostSurveyFlags());
    }

    const toggleExpanded = (id) => {
        const next = new Set(expandedIds);
        next.has(id) ? next.delete(id) : next.add(id);
        setExpandedIds(next);
    }

    const handleCancel = () => {
        setShowCreateSurvey(false);
        setDraft(null);
    };

    const handleGenerateQRCode = (survey) => {
        const url = `https://prmassist.com/caa-survey/${survey._id}/${iata}`;
        const fileName = `${survey._id}-${iata}`
        setLink(url);
        setSurveyTitle(survey.groupTitle)
        setFileName(fileName)
        setShowModal(true);

    }

    const handleClose = () => {
        setShowModal(false);
        setFileName("")
        setLink("")
    }

    const handlePreview = (survey) => {
        const lang = survey.languages?.[0]?.value || "en";
        window.open(`/survey-preview/${iata}/${survey._id}?lang=${lang}`, "_blank");
    }

    if (showCreateSurvey) {
        return (
            <SurveyQuestionnaireForm handleCancel={handleCancel} initialDraft={draft} />
        )
    }

    if (surveys.length === 0 && questionsStatus === "loading") {
        return <div className='mt-5' style={{ display: 'flex', justifyContent: 'center' }}><CustomSpinner /></div>;
    }

    if (surveys.length === 0) {
        return (
            <div className='mt-5' style={{ textAlign: 'center', padding: '48px 16px', border: `1px dashed ${BORDER}`, borderRadius: 12 }}>
                <FaClipboardList size={40} color={Colors.PRM_LIGHT_BLUE} />
                <p style={{ fontSize: 22, fontWeight: 600, marginTop: 16, marginBottom: 4 }}>No surveys yet</p>
                <p style={{ color: MUTED, marginBottom: 20 }}>Create a survey to collect feedback from passengers or staff.</p>
                <Button variant="primary" onClick={createSurvey}><FaPlus style={{ marginRight: 6 }} /> Create survey</Button>
            </div>
        );
    }

    return (
        <div>
            <div className='mt-3' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                <div>
                    <Title>Surveys</Title>
                    <Subtitle>New surveys are saved as drafts. Activate a survey to start sending it.</Subtitle>
                </div>
                <Button variant="primary" onClick={createSurvey}><FaPlus style={{ marginRight: 6 }} /> Create survey</Button>
            </div>

            {/* survey list */}
            <div className='mt-3'>
                {surveys.map((survey) => {
                    const expanded = expandedIds.has(survey._id);
                    const isPending = pendingId === survey._id && updateSurveyStatus === "loading";
                    return (
                        <div
                            key={survey._id}
                            style={{
                                border: `1px solid ${BORDER}`, borderLeft: `4px solid ${survey.active ? '#2E7D32' : BORDER}`,
                                borderRadius: 10, background: '#fff', marginBottom: 12,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
                                {/* Summary */}
                                <div onClick={() => toggleExpanded(survey._id)} style={{ flex: 1, minWidth: 240, cursor: 'pointer' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {expanded ? <FaChevronDown color={MUTED} /> : <FaChevronRight color={MUTED} />}
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>{survey.groupTitle || 'Untitled survey'}</span>
                                        <StatusPill active={survey.active} />
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, marginLeft: 22, flexWrap: 'wrap', fontSize: 13, color: MUTED }}>
                                        {(survey.languages || []).map((l) => (
                                            <Badge key={l.value} bg="light" text="dark" style={{ border: `1px solid ${BORDER}`, fontWeight: 400 }}>{l.label}</Badge>
                                        ))}
                                        <span>· {survey.questions.length} question{survey.questions.length === 1 ? '' : 's'}</span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <CustomTooltip text={"Preview the survey as passengers will see it (opens in a new tab)."} placement="top">
                                        <Button variant="outline-secondary" size="sm" onClick={() => handlePreview(survey)}>
                                            <FaRegEye style={{ marginRight: 6 }} /> Preview
                                        </Button>
                                    </CustomTooltip>

                                    {/* The QR link opens a working survey, so only share it once the survey is live. */}
                                    <CustomTooltip text={survey.active ? "Show a QR code and link passengers can use to complete this survey." : "Activate this survey to share it by QR code."} placement="top">
                                        <span style={{ display: 'inline-block' }}>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={!survey.active}
                                                style={!survey.active ? { pointerEvents: 'none' } : undefined}
                                                onClick={() => handleGenerateQRCode(survey)}
                                            >
                                                <FaQrcode style={{ marginRight: 6 }} /> QR code
                                            </Button>
                                        </span>
                                    </CustomTooltip>

                                    <CustomTooltip text={"Start a new draft with the same settings and questions."} placement="top">
                                        <Button variant="outline-secondary" size="sm" onClick={() => duplicateSurvey(survey)}>
                                            <FaRegCopy style={{ marginRight: 6 }} /> Duplicate
                                        </Button>
                                    </CustomTooltip>

                                    <CustomTooltip text={survey.active ? "This survey will not be sent after deactivation." : "This survey will be sent after activation."} placement="top">
                                        <Button
                                            variant={survey.active ? "outline-danger" : "success"}
                                            size="sm"
                                            style={{ minWidth: 100 }}
                                            disabled={isPending}
                                            onClick={() => activateSurvey(survey._id, !survey.active)}
                                        >
                                            {isPending ? <CustomSpinner /> : survey.active ? "Deactivate" : "Activate"}
                                        </Button>
                                    </CustomTooltip>

                                    <CustomTooltip text={survey.active ? "Deactivate this survey before deleting it." : "Delete this survey."} placement="top">
                                        {/* span keeps the tooltip working while the button is disabled */}
                                        <span style={{ display: 'inline-block' }}>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                aria-label={`Delete ${survey.groupTitle || 'survey'}`}
                                                disabled={survey.active}
                                                style={survey.active ? { pointerEvents: 'none' } : undefined}
                                                onClick={() => setDeleteTarget(survey)}
                                            >
                                                <FaRegTrashCan />
                                            </Button>
                                        </span>
                                    </CustomTooltip>
                                </div>
                            </div>

                            {expanded && (
                                <div style={{ borderTop: `1px solid ${BORDER}`, padding: '8px 16px 16px' }}>
                                    {survey.questions.map((q, i) => (
                                        <div key={i} style={{ padding: "5px" }}>
                                            <QuestionComponent question={q} questionIndex={i + 1} active={false} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            <ActionsModal
                show={!!deleteTarget}
                handleClose={closeDelete}
                title={`Delete "${deleteTarget?.groupTitle || 'survey'}"?`}
                subtitle={"It will be removed from this list and can't be activated again. Answers already collected are kept and stay available in the Surveys tab."}
                minWidth="445px"
                maxWidth="445px"
            >
                {deleteSurveyStatus === "failed" && (
                    <p style={{ color: '#C0392B', fontSize: 14 }}>Couldn't delete the survey — please try again.</p>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button variant="outline-secondary" onClick={closeDelete} disabled={deleteSurveyStatus === "loading"}>Cancel</Button>
                    <Button variant="danger" style={{ minWidth: 120 }} onClick={confirmDelete} disabled={deleteSurveyStatus === "loading"}>
                        {deleteSurveyStatus === "loading" ? <CustomSpinner /> : "Delete survey"}
                    </Button>
                </div>
            </ActionsModal>

            <ActionsModal show={showModal} handleClose={handleClose} title={`QR code: ${surveyTitle}`} subtitle={"Passengers scan this to complete the survey. Copy the link, download the QR image, or open the survey to check it."} minWidth="445px" maxWidth="445px" >
                <CustomQrCode link={link} showInstructions={false} instructions={""} filename={fileName} />
                <div style={{ marginTop: 12, fontSize: 12, color: MUTED, wordBreak: 'break-all', textAlign: 'center' }}>{link}</div>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                    <Button variant="outline-primary" size="sm" href={link} target="_blank" rel="noopener noreferrer">
                        <FaArrowUpRightFromSquare style={{ marginRight: 6 }} /> Open survey
                    </Button>
                </div>
            </ActionsModal>
        </div>
    );
};

export default ReviewControlPanel;
